import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useVrm } from '../../context/VrmContext';
import {
  getPreviewQuality,
  subscribePreviewQuality,
  togglePreviewQuality,
  type PreviewQuality,
} from '../../lib/previewQuality';
import Spinner from '../Spinner';
import { isUnityBridgeReady } from './unityBridge';
import VrmaViewerThree from './VrmaViewerThree';
import VrmaViewerUnity from './VrmaViewerUnity';
import { PLAY_BTN_MIN_DURATION_SEC, SEEK_MIN_DURATION_SEC } from './types';
import type { VrmaBackendHandle, VrmaBackendStatus, VrmaPreviewHandle } from './types';

// dev 環境限定の未公開機能フラグ。本番ビルドでは 'false' が渡される。
const richPreviewEnabled = import.meta.env.VITE_ENABLE_RICH_PREVIEW !== 'false';

interface Props {
  /** 再生する .vrma の URL（任意）。未指定ならファイル選択で読み込む。 */
  vrmaUrl?: string;
  /** リッチ版とのトグル UI を出すか。既定 true。Upload.tsx はライト版固定にするため false を渡す。 */
  showQualityToggle?: boolean;
  /** 指定すると設定・可用性に関わらずこの品質に固定する（トグル UI とは独立）。 */
  forceQuality?: PreviewQuality;
}

interface LoadedInfo {
  hasVrm: boolean;
  hasClip: boolean;
  durationSec: number;
}

// 秒数を m:ss 形式の文字列に整形する。
function formatTime(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * VRMA プレビューの親コンポーネント。UI・再生状態・VrmContext 連携を持ち、実際の描画は
 * バックエンド（ライト版 = three.js の VrmaViewerThree / リッチ版 = Unity WebGL の
 * VrmaViewerUnity）に委ねる。
 *
 * 両バックエンドは常に同じ vrmUrl/vrmaSource/armed/playing/scrubbing/rendering を
 * 受け取る（=常に同じものを読み込もうとする）。表示するのはどちらか一方だけで、
 * 「今どちらを表示しているか」は showThree/showUnity で切り替える。
 */
const VrmaPreview = forwardRef<VrmaPreviewHandle, Props>(function VrmaPreview(
  { vrmaUrl, showQualityToggle = true, forceQuality },
  ref,
) {
  const { vrmUrl, vrmLabel, isCustom, ready, setCustomVrm } = useVrm();

  const rootRef = useRef<HTMLDivElement>(null);
  const threeRef = useRef<VrmaBackendHandle>(null);
  const unityRef = useRef<VrmaBackendHandle>(null);

  const [status, setStatus] = useState<VrmaBackendStatus>('idle');
  const [error, setError] = useState('');
  const [hasVrm, setHasVrm] = useState(false);
  const [hasClip, setHasClip] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  // handleProgress からも同期更新する ref ミラー。バックエンド切り替え時に「今の再生位置」を
  // 状態更新を待たずに同期的に読みたい箇所（consumePendingSeek 呼び出し元）で使う。
  const elapsedRef = useRef(0);
  // シークバーのドラッグ・クリック中かどうか（render ループからの上書き防止のためバックエンドへ渡す）。
  const [scrubbing, setScrubbing] = useState(false);
  // VRM の読み込みを開始してよいか。初回 overlay でのユーザー操作、または props.vrmaUrl の
  // 到着（下の effect）で true になる。
  const [armed, setArmed] = useState(false);
  // ユーザーが選択した VRMA ファイル。props.vrmaUrl より優先する。
  const [pickedVrma, setPickedVrma] = useState<File | undefined>(undefined);

  // ---- 品質設定（ライト/リッチ）----
  const [quality, setQuality] = useState<PreviewQuality>(() => getPreviewQuality());
  useEffect(() => subscribePreviewQuality(setQuality), []);
  // Unity が使えないと判明した理由（WebGL2 非対応・build.json 取得失敗・起動タイムアウト・
  // 致命的エラーなど）。立ったら forceQuality が無い限りライト版に強制フォールバックする。
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  // Unity の起動完了（ready イベント）フラグ。true になった時点でライト版をアンマウントし
  // Unity 側の canvas を表示する（プログレッシブ差し替え）。一度 true になったら、この
  // VrmaPreview が生きている間は戻さない（トグルで rich に戻したときに毎回 3〜8 秒待たせ
  // ないため。以後の rich⇔light 切り替えは Unity 側コンポーネントの mount/unmount のみで
  // 済み、Unity インスタンス自体は lib/unityViewer.ts のシングルトンにキャッシュされている）。
  // 初期値は unityBridge.ts の起動済みフラグから取る: Unity インスタンスはシングルトンで
  // 使い回されるため `ready` イベントはアプリのライフタイムで最初の起動時にしか飛んでこず、
  // ページ遷移で VrmaPreview が再マウントされても再送されない。false 固定のままだと、
  // 一度リッチ版に切り替えた後に別ページへ遷移した際、Unity 側は起動済みなのに
  // unityReady だけが初期値 false に戻ってしまい、ライト版から永久に切り替わらなくなる。
  const [unityReady, setUnityReady] = useState(() => isUnityBridgeReady());
  const [loadProgress, setLoadProgress] = useState<{ ratio: number; phase: string } | null>(null);

  // フラグ無効時は forceQuality や localStorage の保存値に関わらず必ずライト版にする
  // （Unity パスへ一切入らないことをコードで保証するため）。
  const effective: PreviewQuality = !richPreviewEnabled
    ? 'light'
    : (forceQuality ?? (quality === 'rich' && !blockedReason ? 'rich' : 'light'));
  // ライト版は「rich が指定されているが Unity がまだ表示可能になっていない」あいだも表示し
  // 続ける（Unity の初期化中に既に見えている絵をスピナーで潰さないため）。
  const showThree = effective === 'light' || (effective === 'rich' && !unityReady);
  const showUnity = effective === 'rich';
  // useImperativeHandle 等、クロージャで「今どちらが表示中か」を最新のまま読みたい箇所用。
  const showThreeRef = useRef(showThree);
  showThreeRef.current = showThree;

  // バックエンド切り替え時に引き継ぐ再生位置（秒）。切り替えの起点（自動差し替え／手動
  // トグル）で elapsedRef.current を積み、切り替え先の onLoaded 到着時に消費する。
  const pendingSeekRef = useRef<number | null>(null);

  function activeBackendHandle(): VrmaBackendHandle | null {
    return showThreeRef.current ? threeRef.current : unityRef.current;
  }

  function consumePendingSeek(target: VrmaBackendHandle | null, durationSec: number) {
    if (pendingSeekRef.current == null) return;
    const t = Math.min(pendingSeekRef.current, durationSec);
    pendingSeekRef.current = null;
    target?.seek(t);
    setElapsed(t);
    elapsedRef.current = t;
  }

  // props の vrmaUrl が（後から）渡ってきたら自動で読み込みを開始する。
  // Detail.tsx は復号完了後に、Upload.tsx はファイル選択後に URL をセットするため、
  // 初期値が undefined/'' でも後から armed になる。
  useEffect(() => {
    if (vrmaUrl) setArmed(true);
  }, [vrmaUrl]);

  // Upload.tsx は未選択時に vrmaUrl === '' を渡すため、バックエンドには undefined として渡す。
  const vrmaSource = pickedVrma ?? (vrmaUrl || undefined);

  // ---- 可視性ゲート: タブが非表示、またはビューアが画面外にあるあいだは描画を止める ----
  const [documentVisible, setDocumentVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const onVisibilityChange = () => setDocumentVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const [inViewport, setInViewport] = useState(true);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setInViewport(entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rendering = documentVisible && inViewport;

  const handleStatusChange = useCallback((s: VrmaBackendStatus) => {
    setStatus(s);
    if (s === 'loading') setError('');
  }, []);

  const applyLoadedInfo = useCallback((info: LoadedInfo) => {
    setHasVrm(info.hasVrm);
    setHasClip(info.hasClip);
    setDuration(info.durationSec);
    setElapsed(0);
    elapsedRef.current = 0;
    // 再生/一時停止ボタンを出さない短尺クリップは、一時停止状態のままだと再開手段が
    // 無くなるため必ず再生状態に戻す（バックエンド側でも paused を解除している）。
    if (info.hasClip && info.durationSec > 0 && info.durationSec <= PLAY_BTN_MIN_DURATION_SEC) {
      setPlaying(true);
    }
  }, []);

  const handleProgress = useCallback((t: number) => {
    elapsedRef.current = t;
    setElapsed(t);
  }, []);
  const handleError = useCallback((message: string) => setError(message), []);

  // ---- ライト版（three.js）用ハンドラ。常に共有 state に反映する ----
  const handleThreeLoaded = useCallback(
    (info: LoadedInfo) => {
      applyLoadedInfo(info);
      consumePendingSeek(threeRef.current, info.durationSec);
    },
    [applyLoadedInfo],
  );

  // ---- リッチ版（Unity）用ハンドラ。unityReady になるまでは背後で読み込み中なので、
  // 共有 state（ローディング表示・エラー表示等）には反映しない。反映するとライト版が
  // 問題無く表示できているのに Unity の初期化都合でスピナーやエラーが上書き表示されてしまう。
  const handleUnityStatusChange = useCallback(
    (s: VrmaBackendStatus) => {
      if (!unityReady) return;
      handleStatusChange(s);
    },
    [unityReady, handleStatusChange],
  );
  const handleUnityLoaded = useCallback(
    (info: LoadedInfo) => {
      if (!unityReady) return;
      applyLoadedInfo(info);
      consumePendingSeek(unityRef.current, info.durationSec);
    },
    [unityReady, applyLoadedInfo],
  );
  const handleUnityProgress = useCallback(
    (t: number) => {
      if (!unityReady) return;
      handleProgress(t);
    },
    [unityReady, handleProgress],
  );
  const handleUnityError = useCallback(
    (message: string) => {
      if (!unityReady) return;
      handleError(message);
    },
    [unityReady, handleError],
  );
  const handleUnityUnsupported = useCallback((reason: string) => {
    setBlockedReason(reason);
  }, []);
  const handleLoadProgress = useCallback((ratio: number, phase: string) => {
    setLoadProgress({ ratio, phase });
  }, []);
  const handleUnityReady = useCallback(() => {
    // 今ライト版が表示している再生位置を、差し替え後の Unity 側の onLoaded で引き継ぐ。
    pendingSeekRef.current = elapsedRef.current;
    setUnityReady(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      resetCamera: () => activeBackendHandle()?.resetCamera(),
    }),
    [],
  );

  const onPickVrm = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      // IndexedDB に保存 → Context の vrmUrl が更新される。armed も立てて反映を確実にする。
      await setCustomVrm(f);
      setArmed(true);
    } catch (err) {
      console.error(err);
      setError('VRM の保存に失敗しました。別のファイルを選択してください。');
      setStatus('error');
    }
  };
  const onPickVrma = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPickedVrma(f);
    setArmed(true);
  };

  // シークバーのドラッグ・クリックで再生位置を変更する。一時停止中でもポーズを即座に反映させる。
  function seek(t: number) {
    const clamped = Math.min(Math.max(t, 0), duration);
    activeBackendHandle()?.seek(clamped);
    setElapsed(clamped);
    elapsedRef.current = clamped;
  }

  // トグル操作。押した瞬間の再生位置を控えて、切り替え先バックエンドの onLoaded で引き継ぐ。
  // 保存・購読者への通知（quality state の更新はそれ経由で行われる）は previewQuality.ts に委ねる。
  function handleToggleQuality() {
    pendingSeekRef.current = elapsedRef.current;
    setNoticeDismissed(false);
    togglePreviewQuality();
  }

  const isRich = effective === 'rich';
  const showNotice = !!blockedReason && !noticeDismissed;
  // Unity 起動中（ready 未受信）のあいだだけ、細い進捗バーで知らせる。ready 到達後の
  // アセット読み込みは既存の VRMA 未選択オーバーレイ等で表現されるため、ここでは扱わない。
  const showLoadBar = showUnity && !unityReady;
  const loadBarLabel = !loadProgress
    ? 'リッチ版を読み込み中…'
    : loadProgress.ratio >= 1
      ? '起動中…'
      : `リッチ版を読み込み中… ${Math.round(loadProgress.ratio * 100)}%`;

  if (!ready) {
    return (
      <div className="viewer" ref={rootRef}>
        <div className="viewer__overlay">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className={`viewer${showNotice ? ' viewer--noticed' : ''}`} ref={rootRef}>
      {showThree && (
        <VrmaViewerThree
          ref={threeRef}
          vrmUrl={vrmUrl}
          vrmaSource={vrmaSource}
          armed={armed}
          playing={playing}
          scrubbing={scrubbing}
          rendering={rendering}
          onStatusChange={handleStatusChange}
          onLoaded={handleThreeLoaded}
          onProgress={handleProgress}
          onError={handleError}
        />
      )}

      {showUnity && (
        <div className={unityReady ? undefined : 'viewer__layer--hidden'}>
          <VrmaViewerUnity
            ref={unityRef}
            vrmUrl={vrmUrl}
            vrmaSource={vrmaSource}
            armed={armed}
            playing={playing}
            scrubbing={scrubbing}
            rendering={rendering}
            onStatusChange={handleUnityStatusChange}
            onLoaded={handleUnityLoaded}
            onProgress={handleUnityProgress}
            onError={handleUnityError}
            onUnsupported={handleUnityUnsupported}
            onLoadProgress={handleLoadProgress}
            onReady={handleUnityReady}
          />
        </div>
      )}

      {!hasVrm && status !== 'loading' && (
        <div className="viewer__overlay">
          <svg width="44" height="44" viewBox="0 0 100 100" aria-hidden="true">
            <rect x="13" y="49" width="18" height="11" rx="9" fill="#3eccb4" opacity="0.38" />
            <rect x="41" y="44" width="18" height="16" rx="9" fill="#3eccb4" opacity="0.68" />
            <rect x="69" y="40" width="18" height="20" rx="9" fill="#3eccb4" />
          </svg>
          <p>VRM アバターを読み込むと VRMA を再生できます。</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="btn btn--primary" onClick={() => setArmed(true)}>
              {isCustom ? `${vrmLabel} を読み込む` : 'サンプル VRM を読み込む'}
            </button>
            <label className="btn">
              VRM ファイルを選択
              <input type="file" accept=".vrm,.glb" hidden onChange={onPickVrm} />
            </label>
          </div>
          {error && <div className="viewer__error">{error}</div>}
        </div>
      )}

      {status === 'loading' && (
        <div className="viewer__overlay">
          <Spinner />
        </div>
      )}

      {hasVrm && status !== 'loading' && !hasClip && (
        <div className="viewer__overlay" style={{ background: 'rgba(0,0,0,0.35)' }}>
          <p>VRMA（.vrma）を選択して再生</p>
          <label className="btn btn--primary">
            VRMA ファイルを選択
            <input type="file" accept=".vrma,.glb" hidden onChange={onPickVrma} />
          </label>
          {error && <div className="viewer__error">{error}</div>}
        </div>
      )}

      {hasVrm && hasClip && (
        <div className="viewer__transport">
          {duration >= SEEK_MIN_DURATION_SEC && (
            <div className="viewer__seek">
              <input
                type="range"
                min={0}
                max={duration}
                step={0.01}
                value={elapsed}
                onPointerDown={() => setScrubbing(true)}
                onPointerUp={() => setScrubbing(false)}
                onPointerCancel={() => setScrubbing(false)}
                onBlur={() => setScrubbing(false)}
                onChange={(e) => seek(Number(e.target.value))}
                aria-label="再生位置"
              />
              <span className="viewer__time">
                {formatTime(elapsed)} / {formatTime(duration)}
              </span>
            </div>
          )}
          <div className="viewer__controls">
            {duration > PLAY_BTN_MIN_DURATION_SEC && (
              <button className="btn viewer__play-btn" onClick={() => setPlaying((p) => !p)}>
                {playing ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" fill="currentColor">
                      <rect x="2" y="1" width="3" height="10" />
                      <rect x="7" y="1" width="3" height="10" />
                    </svg>
                    一時停止
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" fill="currentColor">
                      <path d="M2 1 L10 6 L2 11 Z" />
                    </svg>
                    再生
                  </>
                )}
              </button>
            )}
            <label className="btn">
              VRM ファイルを選択
              <input type="file" accept=".vrm,.glb" hidden onChange={onPickVrm} />
            </label>
          </div>
        </div>
      )}

      {showQualityToggle && richPreviewEnabled && (
        <div className="viewer__quality">
          <button
            type="button"
            className="btn viewer__quality-btn"
            aria-label={isRich ? 'ライト版に切り替え' : 'リッチ版に切り替え'}
            title="初回は読み込みに数秒かかります。視点はリセットされます。"
            aria-busy={showLoadBar}
            onClick={handleToggleQuality}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z" />
            </svg>
            {isRich ? 'ライト版' : 'リッチ版'}
          </button>
        </div>
      )}

      {showNotice && (
        <div className="viewer__fallback-notice" role="status" aria-live="polite">
          <span>この環境ではリッチ版を表示できないため、ライト版で再生しています。</span>
          <button type="button" aria-label="通知を閉じる" onClick={() => setNoticeDismissed(true)}>
            ×
          </button>
        </div>
      )}

      {showLoadBar && (
        <div className="viewer__load-bar">
          <div
            className="viewer__load-bar-fill"
            style={{ width: `${Math.round(Math.min(loadProgress?.ratio ?? 0, 1) * 100)}%` }}
          />
          <span>{loadBarLabel}</span>
        </div>
      )}
    </div>
  );
});

export default VrmaPreview;
